/** @module csi/heading/AttributeController */
define([
	"dojo/_base/declare",
	"dojo/dom",
	"dojo/topic",
	"csi",
	"csi/_base/underscore",
	"csi/heading/_base",
	"csi/heading/Controller",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/types",
	"csi/xml"
], function(declare, dom, topic, csi, _, csiHeading, Controller, reflection, reflectionHints, types, xml) {

	return declare(Controller, {
		// Any: old and new values for attribute
		oldValue: null,
		newValue: null,

		// Boolean: indicates whether when cleaning values from a map to remove the entire entry instead of leaving key:<empty>
		bRemoveKey: false,

		// Boolean: indicates what refresh function to use from postProcessSuccess
		bRefreshNode: false,

		// Boolean: flag to indicate if need to validate node when changing this attribute
		bValidateNode: false,

		// String|Number: key of value in container attribute if editing single value
		keyContainer: null,

		// String: result node id where change is made (for hierarchical tables, this is not just the node URL)
		refNodeId: null,

		// Context: context for controller (FIX: may want to move this to base csi.action.Controller)
		_ctx: null,

		/**
		 * @override
		 * Initialize class members
		 */
		start: function() {
			var bHold = this.bHold;
			var h = this.heading;
			var rca = h && h._rca;
			var refNode = this.widget.getRefNode(this.m_element);
			if (refNode) {
				this.refNodeId = this.widget.getRefNodeId(refNode);
			}

			// store context and existing value before starting
			this._ctx = this.getContext();
			this.oldValue = this.getOldValue();

			// if notify logic or dependents on attribute, then do not hold changes and refresh node by default - assuming that more than just
			// the one attribute value on the node can change
			if (rca && (rca.hasNotifyLogic || rca.getDependents().length)) {
				this.bHold = false;
				this.bRefreshNode = true;
			}

			// if inline editing node name and class has validation rules, then assumption is that this
			// is for node uniqueness and changes should not be held (just to be safe)
			if (h.m_id == xml.ATTR_NAME) {
				var rc = reflection.getNodeClass(this.getNode());
				if (rc && rc.hasNodeValidation) {
					this.bHold = false;
					this.bValidateNode = true;
				}
			}

			this.inherited(arguments);
		},

		_startTracking: function() {
			// summary: make sure refNode is valid during action - it's possible DOM changed
			// FIX: is this only needed on resumeProcess, or even at start of controller?
			if (this.refNode && !dom.isDescendant(this.refNode, this.widget.domNode)) {
				this.refNode = null;
				this._findRefNode();
			}

			this.inherited(arguments);
		},

		getContext: function() {
			// summary: apply container key if any to context
			var ctx = this.inherited(arguments);
			ctx.keyValue = this.keyContainer;
			return ctx;
		},

		getCopyValue: function() {
			// summary: return value for attribute being copied
			return this.oldValue;
		},

		getOldValue: function() {
			// summary: return previous value for attribute
			return this.ren._getValue(this._ctx, this.heading);
		},

		getReturnNodeRequest: function(/*String[]*/urlsNew) {
			// summary: add paths to request
			var req = this.inherited(arguments);
			if (req) {
				var refNode = this.widget.getRefNode(this.refNode);
				req.addDependencyPaths(this.widget.getRefNodePaths(refNode));
				this.bRefreshNode = true;
			}

			return req;
		},

		/**
		 * @override
		 * Wait for view to refresh before handling any pending action
		 */
		onEnd: function() {
			// cleanup circular reference in context
			if (this._ctx) {
				this._ctx.ac = null;
			}

			if (this.isValidAction() && (this.endStatus !== false)) {
				var view = this.widget;
				view.when(view.getDeferred(), this._onEndHandlePendingAction.bind(this));
			}
		},

		toWriter: function() {
			// summary: write operations for new attribute value
			var h = this.heading;
			var id = h.m_id;
			var node = this.getNode();
			var type = this._getValueType(node, id);
			var bClientNode = xml.isClientUrl(node.$URL);
			var call;

			if (bClientNode) {
				// apply change to client node and notify that attribute value was changed
				var value = this.newValue;

				if (xml.attributeTypeIsContainer(type)) {
					value = _.clone(node[id]);

					if (this.keyContainer != null) {
						value[this.keyContainer] = this.newValue;
					}
				}

				xml.nodeAttributeSetValue(node, id, value, true);
				this._notifyOfChange(null, node, id);
			} else {
				call = this._toWriterAttribute(id, type);

				// update any dependent attributes if any changes were made
				// FIX: not sure if calling isChanged is correct - it is possible that other inline edits were
				// done already that caused the changes; probably better to compare oldValue and newValue, but
				// this currently does not work for csi/heading/AggregateController where newValue can be empty
				if (this.m_writer.isChanged()) {
					this._toWriterDependencies(node, h, type);
				}
			}

			// add call to validate node if necessary
			if (this.bValidateNode) {
				this.validateNode(this.m_urlNode, false);
			}

			// check for other dependencies to determine if more than single heading needs to be refreshed
			if (!this.bRefreshNode) {
				this.bRefreshNode = this.widget.hasRefNodeDependency(node, h, type);
			}

			return call;
		},

		/**
		 * Maintain scroll position if no pending action; InlineController will handle the pending action
		 */
		_onEndHandlePendingAction: function() {
			if (!this.hasPendingAction()) {
				this._postProcessMaintainScroll();
			}
		},

		_toWriterAttribute: function(/*String*/id, /*String*/type) {
			// summary: write out operations to set value
			if (this.m_action.m_request) {
				if (this.keyContainer != null) {
					type = xml.attributeTypeGetBase(type) || type;
				}

				return this.toWriterRequest().addParameter(xml.PROP_VALUE, type, this.newValue);
			}

			var cn = this._toWriterValue(id, type);
			var node = this.getNode();

			// disable expression on attribute since value is explicitly being set
			// DE12336: do the same for any sub-headings or parent heading with expressions - currently only support those without any path
			var h = this.heading;

			if (h.m_parent && !h.isMatrixSubHeading()) {
				h = h.m_parent;
			}

			if (h.m_id && xml.nodeAttributeHasExpression(node, h.m_id, true) && !xml.nodeAttributeHasFlag(node, h.m_id, xml.ATTR_FLAG_NOEDIT)) {
				//[YG] DE18947 solution is to avoid disabling expression when attribute is read-only
				cn.disableAttributeExp(h.m_id);
			}

			_.each(h.m_subheadings, function(sh) {
				if (h.m_id && xml.nodeAttributeHasExpression(node, h.m_id, true) && !xml.nodeAttributeHasFlag(node, h.m_id, xml.ATTR_FLAG_NOEDIT)) {
					//[YG] DE18947 solution is to avoid disabling expression when attribute is read-only
					cn.disableAttributeExp(h.m_id);
				}
			}, this);

			// notify that attribute value was changed
			this._notifyOfChange(cn, node, id);

			return null;
		},

		_toWriterValue: function(/*String*/id, /*String*/type) {
			// summary: set attribute value and disable expression if one
			var cn = this._toWriterModifiedByClient(id);
			var value = this.newValue;

			if (this.keyContainer == null) {
				cn.changeAttribute(id, type, value);
			} else if (!value && ((id == xml.ATTR_LOCALIZED_NAME) || this.bRemoveKey)) {
				cn.removeAttributeKeys(id, type, [this.keyContainer]);
			} else {
				cn.changeAttributeKeys(id, type, csi.objectNew(this.keyContainer, value));
			}

			return cn;
		},

		_toWriterModifiedByClient: function(/*String*/id) {
			return this.m_writer.changeNode(this.m_urlNode).changeAttributeFlag(id, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
		},

		_toWriterDependencies: function(/*Node*/node, /*Heading*/heading, /*String*/type) {
			// summary: analyze heading dependencies
			var id = heading.m_id;
			if (!id) return;

			// get any headings that have dependency to given heading - dependency must exist on same path
			// FIX: this doesn't currently account for images - should it?
			var headings = heading.isMatrixSubHeading() ? heading.m_parent.m_subheadings : this.widget.defView.getHeadings();
			if (!headings) return;

			var path = heading.m_path;
			var dep = [];
			var depType;
			var h, i = 0, j;

			while (h = headings[i++]) {
				if (!h.isMatrixGroup && xml.pathsAreSame(h.m_path, path)) {
					if (h.m_dependency == id) {
						dep.push(h);
					} else if (h.m_subheadings) {
						// check sub-headings - they inherit path for parent heading, so no need to check child
						j = 0;

						var sh;
						while (sh = h.m_subheadings[j++]) {
							if (sh.m_dependency == id) {
								dep.push(sh);
							}
						}
					}
				}
			}

			_.each(dep, function(h) {
				if (depType = h.getAttrType()) {
					if ((depType.indexOf(xml.ATTR_REF) == 0) || (h.m_format == types.FORMAT_SELECTION)) {
						// if one ref attribute is dependent on another, then whenever the first changes, the second must be reset;
						// don't actually write anything is special request to server is being used
						if (this._shouldResetDependencies() && (type.indexOf(xml.ATTR_REF) == 0)) {
							this.m_writer.changeNode(this.m_urlNode).changeAttribute(h.m_id, depType, xml.attributeTypeGetDefaultValue(depType));
						}

						// it is possible the heading used queries based on the published attribute, so refresh cache for dependent heading
						this.widget.refreshHeadingStore(h, this.m_urlNode);
					}

					// now process any dependencies of this dependency
					this._toWriterDependencies(node, h, depType);
				}

				// if the dependency is outside the heading being changed, then refresh the display of this node
				this.bRefreshNode |= !this.heading.hasSubHeading(h);
			}, this);
		},

		_notifyOfChange: function(/*ChangeNode?*/cn, /*Node*/node, /*String*/id, /*Boolean*/bRestore, /*Boolean*/bDisable) {
			// summary: notify that attribute value was changed
			var bClientNode = xml.isClientUrl(node.$URL);
			var f = reflectionHints.getWatchFunction(node.$Type, id);
			if (f) {
				if (!bClientNode) {
					cn = cn || this.m_writer.changeNode(node.$URL);
				}

				try {
					if (f(this, cn, node, id, bRestore, bDisable)) {
						this.bRefreshNode = true;
					}
				} catch (err) {
					throw new Error(`Error in watchAttribute for ${node.$Type}.${id}: ${err.message}`);
				}
			}
		},

		postProcess: function(/*Boolean*/bStatus) {
			// summary: handle exception while publishing
			if (bStatus === false) {
				if (this.bHold) {
					// if exception on delayed attribute changes, then refresh page since other nodes/attributes
					// may have already been changed
					this.widget.refreshWidget();
				} else if (this.bRefreshNode) {
					// refresh node if change to attribute and dependencies caused exception
					this._refreshNode();
				} else {
					// refresh attribute if exception
					this._refresh(false);
				}
			} else if (bStatus) {
				// notify those interested of change to attribute
				if ((this.newValue != null) && (this.oldValue != null)) {
					topic.publish("/csi/attribute/change", this.getNode(), this.heading.m_id, this.oldValue, this.newValue);
				}
			}

			this.inherited(arguments);
		},

		postProcessSuccess: function() {
			// summary: call Heading/@PostChange until migrated to PostAction
			var dfd = this.inherited(arguments);
			if (dfd) {
				// if deferred returned from PostAction, then assume heading will get refreshed automatically
				this.refNode = null;
			} else if (this.bRefreshNode) {
				// refresh node in view when change to attribute triggers changes to other attributes
				this._refreshNode();
			} else {
				// refresh attribute
				this._refresh(true);
			}
		},

		_findRefNode: function() {
			// summary: when DOM is refreshed, might need to find refNode based on context
			if (!this.refNode || !this.refNode.offsetHeight) {
				var view = this.widget;
				var h = this.heading;

				if (h.m_parent && !h.isMatrixSubHeading()) {
					h = h.m_parent;
				}

				// find node where heading is rendered
				var refNodeId = this.refNodeId;
				var nl = csiHeading.findHeadingNodes(view.domNode, h, this.m_urlNode, this.keyContainer);
				if (nl && refNodeId) {
					nl = nl.filter(function(domNode) {
						var refNode = view.getRefNode(domNode);
						return refNodeId == view.getRefNodeId(refNode);
					});
				}

				if (nl) {
					this.refNode = nl[0];
				}
			}

			return this.refNode;
		},

		_getValueType: function(/*Node*/node, /*String*/id) {
			// FIX: can we just use "this.ren.type" here? Otherwise might cause problems with editing container values (keyValue)
			return xml.nodeAttributeGetType(node, id) || this.ren.type;
		},

		_isValueChanged: function() {
			// summary: by default, just compare old value vs. new value
			return (this.newValue !== null) && (this.newValue !== this.oldValue);
		},

		_postProcessMaintainScroll: function() {
			// summary: set minimum height for view to make sure scroll bar does not change when view is refreshed
			if (this.isValidAction()) {
				var refNode = this._findRefNode();
				if (refNode) {
					this.widget.scrollIntoView(refNode);
				}
			}
		},

		_refresh: function(/*Boolean*/bStatus) {
			// summary: re-render heading - use the root heading to re-render if it's a merged cell; could have edited sub-heading
			const {heading, keyContainer, refNode, m_urlNode, widget, _ctx} = this;
			const {m_parent} = heading;

			if (_ctx) {
				// ensure that rollup expressions are recalculated when refreshing; this is usually not needed as cells that are edited
				// will no longer have expressions, but it is possible the parent heading or sub-headings could have expressions (such as Order.UnitsPerSize)
				_ctx.preRollup();

				// if path to changed attribute is not composite, then should tell view to refresh all occurrences of same attribute;
				// refresh all others first so that omitting refNode from first pass works (in comparison mode, refresh could apply to
				// whole row and thus refNode is no longer correct)
				if (keyContainer == null) {
					widget.refreshHeading(heading, m_urlNode, refNode);
				}

				// determine heading to refresh
				let headingToRefresh = heading;

				if (m_parent && !m_parent.isMatrixGroup) {
					headingToRefresh = m_parent;
				}

				// refresh cell being edited
				widget.refreshHeadingCell(_ctx, headingToRefresh, refNode, bStatus && this.hasPendingAction());

				_ctx.postRollup();
			} else {
				console.warn("_end() called before refresh!");
			}
		},

		/**
		 * Refresh all results where heading is rendered in view
		 */
		_refreshNode: function() {
			var refNode = this.refNode;
			const refNodes = [];
			var view = this.widget;
			var nl = csiHeading.findHeadingNodes(view.headingsNode || view.domNode, this.heading, this.m_urlNode, this.keyContainer);

			function add(domNode) {
				const _refNode = view.getRefNode(domNode);
				if (_refNode) {
					refNodes.push(_refNode);
				}
			}

			_.each(nl, add);
			add(refNode);

			if (!refNodes.length) {
				refNodes.push(view.domNode);
			}

			_.each(_.uniq(refNodes), view.redrawNode, view);

			// find refNode in refreshed view now that old refNode is no longer in DOM
			this.refNode = null;
			this._findRefNode();
		},

		_shouldResetDependencies() {
			return !this.m_action.m_request;
		}
	});
});
